import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertCircle } from 'lucide-react';
import { convertToEmbedUrl, getVideoProvider } from '@/utils/videoEmbedUtils';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: {
    title: string;
    description?: string;
    embed_url?: string;
    duration: number;
    category: string;
  };
}

export const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  activity,
}) => {
  const embedUrl = activity.embed_url ? convertToEmbedUrl(activity.embed_url) : null;
  const videoProvider = activity.embed_url ? getVideoProvider(activity.embed_url) : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold mb-2">
            {activity.title}
          </DialogTitle>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Badge variant="outline" className="text-xs">
              {activity.category}
            </Badge>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {activity.duration} minutes
            </div>
            {videoProvider && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                {videoProvider}
              </span>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-4">
          {activity.description && (
            <p className="text-sm text-muted-foreground">
              {activity.description}
            </p>
          )}
          
          {embedUrl ? (
            <div className="aspect-video w-full">
              <iframe
                src={embedUrl}
                title={activity.title}
                className="w-full h-full rounded-lg border"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="aspect-video w-full bg-muted rounded-lg flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Video not available</p>
                <p className="text-xs">The video URL could not be loaded</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};